import type {
  Emotion,
  FacialFeature,
  BaseShape,
} from '@airhead-games/shared/configs';
import { getAvailableShapeNames } from '@airhead-games/shared/configs';
import {
  getBaseShape,
  EMOTION_TRANSFORMS,
  type FaceTransform,
} from '@airhead-games/shared/avatar';
export { EMOTION_TRANSFORMS } from '@airhead-games/shared/avatar';
export type {
  FaceTransform,
  EmotionTransform,
} from '@airhead-games/shared/avatar';

// Transform merging utilities
/**
 * Merge emotion transforms with reaction transforms
 */

export function mergeTransforms(
  emotionTransform: FaceTransform,
  reactionTransform: FaceTransform = {
    shiftX: 0,
    shiftY: 0,
    scale: 1,
    rotation: 0,
  }
): FaceTransform {
  return {
    shiftX: emotionTransform.shiftX + reactionTransform.shiftX,
    shiftY: emotionTransform.shiftY + reactionTransform.shiftY,
    scale: emotionTransform.scale * reactionTransform.scale,
    rotation: emotionTransform.rotation + reactionTransform.rotation,
  };
}

/**
 * Get merged face transforms for an emotion with optional reaction transforms
 */

export function getEmotionFaceTransforms(
  emotion: Emotion,
  reactionTransforms?: {
    leftEye: FaceTransform;
    rightEye: FaceTransform;
    mouth: FaceTransform;
  }
): {
  leftEye: FaceTransform;
  rightEye: FaceTransform;
  mouth: FaceTransform;
} {
  const emotionTransform = EMOTION_TRANSFORMS[emotion];

  return {
    leftEye: mergeTransforms(
      emotionTransform.eyes,
      reactionTransforms?.leftEye
    ),
    rightEye: mergeTransforms(
      emotionTransform.eyes,
      reactionTransforms?.rightEye
    ),
    mouth: mergeTransforms(emotionTransform.mouth, reactionTransforms?.mouth),
  };
}

// Shape selection utilities
/**
 * Get a base shape by name and feature type
 */

export function getRandomBaseShape(facialFeature: FacialFeature): BaseShape {
  const availableNames = getAvailableShapeNames(facialFeature);
  const randomName =
    availableNames[Math.floor(Math.random() * availableNames.length)];
  const shape = getBaseShape(facialFeature, randomName);

  if (!shape) {
    throw new Error(
      `Failed to find shape "${randomName}" for feature "${facialFeature}"`
    );
  }

  return shape;
}
